import {DatasetIdentifier} from "./datasetModels";

export type TaskTemplate = {
    id?: number,
    title: string,
    versions: Array<any>,
    createdBy: string,
    modifiedBy: string,
    createdOn: string,
    modifiedOn: string,
}

export type TaskTemplateVersion = {
    id?: string,
    versionTitle: string,
}

export type InstantDoubleValue = {
    key: string,
    value: number,
}

export type DatasetDataInfoResponse = {
    identifier: String,
    title: String,
    unit: String,
    data: Array<InstantDoubleValue>,
    decimalPlaces: number
}

export interface IUsersDataInfoResponse {
    id: number;
    externalId?: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    displayName: string;

    effectivePermissions: Array<string>;
    roles: Array<string>;

    jobTitle: string;
    companyName: string;
    companyId: number;
    companyRef: string;

    email: string;
    emailValidated: boolean;
    phone: string;
    country: string;


    licenseAssignation: IUserLicenseAssignation;
    extraDate:Date;
}

export interface ILicenseDataInfo {
    id: number;
    title: string;
    maxAllowedVersion: string;
    registeredTo: string;


    //validUntil:Instant?
    features: any;
    otherFeatures: any;


    dcNodeCount: number;
    electricBusCount: number;
    hydraulicNodeCount: number;

    maxBus: number;
    maxDCNode: number;
    maxHNode: number;
}

export interface IUserLicenseAssignation {
    licenseId: number;
    expirationDate?: any;
    psmMaxVersion?: string;
}

export type AirflowWorkerQueue = "DEFAULT" | "FAST_COMPUTE";
export type ChronoUnitStr = "SECONDS" | "HOURS" | "DAYS" | "MONTHS" | "WEEKS";

export type DatasetIdentifierStr = string;

export class ProductTemplateTaskSelectionOptions {
    id?: number;
    productTemplateRef?: string;
    entryDataset: DatasetIdentifierStr | null = null;
    useDataset: boolean = false;
    latestEntryCount: number = 10;
}

export interface IProductTemplateInfoDTO {
    id: number;
    refName: string;
    displayName: string;
    companyId: number;
    companyRef: string;
}

export class BaseDatasetDTO {
    identifier: string | null = null;
    title: string | null = null;
    unit: string | null = null;

    companyId: number | null = null;
    categoryId: number | null = null;
    groupId: number | null = null;

    decimalPlaces: number = 1;
    dataType: string | null = null;
    datasetClass: string | null = null;
    accessType: string | null = null;
    deviceId: number | null = null;
    tags: Array<string> = [];

    constructor(data: Partial<BaseDatasetDTO>) {
        if (data) {
            Object.assign(this, data);
        }
    }

    toString(): string {
        return `${this.title} | ${this.unit}`;
    }
}

export class DatasetInfo {
    identifier: string | null = null;
    title: string | null = null;
    unit: string | null = null;
    groupTitle: string | null = null;
    groupId: number | null = null;

    categoryTitle: string | null = null;
    categoryId: number | null = null;

    dataType: string | null = null;
    datasetClass: string | null = null;
    accessType: string | null = null;

    decimalPlaces: number | null = null;
    tags: string[] = []

    constructor(data: Partial<DatasetInfo>) {
        if (data) {
            Object.assign(this, data)
        }
    }

    toString(): string {
        let tmp = this.title || "";

        if (this.unit) {
            tmp += " | " + this.unit;
        }

        if (this.groupTitle) {
            tmp += " | " + this.groupTitle;
        }

        return tmp;
    }
}

export class ScheduledTaskDTO {
    id?: string;
    parentId?: string;
    companyId?: number;
    companyRef?: string;

    title: string = "";
    description: string | null = "";

    useCategories: boolean = false;

    environmentId: string = "default_legacy";
    executorQueue: AirflowWorkerQueue = "DEFAULT";
    queueSlots: number = 1;
    timeout: number = 300;

    downstreamTasks: string[] = [];
    upstreamTasks: string[] = [];

    constants: Record<string, string> = {};
    productTemplates: Record<number, ProductTemplateTaskSelectionOptions> = {};
    freestyleJson: string = "{\n\t\n}";

    indexedInputDatasets: DatasetIdentifierStr[] = [];
    indexedOutputDatasets: DatasetIdentifierStr[] = [];
    namedInputDatasets: Record<string, DatasetIdentifierStr> = {};
    namedOutputDatasets: Record<string, DatasetIdentifierStr> = {};

    inputCategories: Record<string, number> = {};
    outputCategories: Record<string, number> = {};

    categoryDataLag: number = 0;
    categoryDataLagUnit: ChronoUnitStr = "HOURS";
    categoryDataLead: number = 0;
    categoryDataLeadUnit: ChronoUnitStr = "HOURS";

    dataLag: number = 0;
    dataLagUnit: ChronoUnitStr = "HOURS";
    dataLead: number = 0;
    dataLeadUnit: ChronoUnitStr = "HOURS";
    calculationTimeOffset: number = 0;
    calculationTimeOffsetUnit: ChronoUnitStr = "HOURS";
    calculationPeriod: number = 1;
    calculationPeriodUnit: ChronoUnitStr = "HOURS";
    calculationSteps: number = 1;

    extraExecutionRecipients: string[] = [];
    overrideRecipients: boolean = false;

    codeIsZip: boolean = false;
    codeFileName: string | null = null;
    codeFileId: number | null = null;
    code?: string | null = null;

    isTemplated: boolean = false;
    templateDefinition?: TaskTemplateDefinitionDTO = undefined
    inputGroups: Record<string, TaskInputGroup[]> = {};


    constructor(data?: Partial<ScheduledTaskDTO>) {
        if (data) {
            Object.assign(this, data);
        }
    }
}

export class DatasetCategoryDTO {
    id?: number;
    title?: string;
    defaultGroupId?: number;
    companyId?: number;
    defaultDatasetType?: string;
    defaultUnit: string = '';
    isRemote: boolean = false;

    constructor(data?: Partial<DatasetCategoryDTO>) {
        if (data) {
            Object.assign(this, data);
        }
    }

    toString(): string {
        return `${this.title}${this.isRemote ? ' [R]' : ''}`;
    }
}

export type PythonCodeFileDTO = {
    id: number,
    uniqueId: string,
    name: string,
    code: string,
}

export enum TaskTemplateInputType {
    CONSTANT = "CONSTANT",
    PRODUCT_TEMPLATE = "PRODUCT_TEMPLATE",
    DATASET_PAIRS = "DATASET_PAIRS",

    INPUT_INDEXED_DATASETS = "INPUT_INDEXED_DATASETS",
    INPUT_NAMED_DATASET = "INPUT_NAMED_DATASET",
    INPUT_NAMED_CATEGORY = "INPUT_NAMED_CATEGORY",

    OUTPUT_INDEXED_DATASETS = "OUTPUT_INDEXED_DATASETS",
    OUTPUT_NAMED_DATASET = "OUTPUT_NAMED_DATASET",
    OUTPUT_NAMED_CATEGORY = "OUTPUT_NAMED_CATEGORY",

    FREESTYLE_JSON = "FREESTYLE_JSON",
}

export enum TaskTemplateInputSubType {
    STRING = "STRING",
    NUMBER = "NUMBER",
    DATE = "DATE",
    BOOLEAN = "BOOLEAN",
}

export enum TaskTemplateInputGroupType {
    SIMPLE = "SIMPLE",
    GROUPED = "GROUPED",
}

// export enum TaskTemplateInputType {
//     STRING,
//     JSON,
//     NUMBER,
// }

export interface ITaskTemplateInputDefinition {
    id: string;
    title: string;
    info?: string;
    type: TaskTemplateInputType;
    width?: number;
    options?: ITaskTemplateInputOptions
}

export interface ITaskTemplateInputOptions {
    subType?: TaskTemplateInputSubType; // Only for CONSTANT
    selectOptions?: any[]; // Only for CONSTANT
    selectionsOptional?: boolean; // When true, selections are not forced
    default?: any; // Only CONSTANT and JSON
}

export interface TaskTemplateInputFromSubgroup {
    header: string | null,
    inputs?: ITaskTemplateInputDefinition[],
}

export interface TaskTemplateInputGroupDefinition {
    id: string;
    type: TaskTemplateInputGroupType
    header: string;
    subForms: TaskTemplateInputFromSubgroup[]
}

export interface TaskTemplateDefinitionDTO {
    inputGroupDefinitions: TaskTemplateInputGroupDefinition[];
}

export interface IDatasetInputOutputPair {
    input: DatasetIdentifier,
    output: DatasetIdentifier,
}


export class TaskInputGroup {
    isGrouped: boolean = false;
    title?: string;

    constants: Record<string, string> = {};
    productTemplates: Record<string, ProductTemplateTaskSelectionOptions> = {};

    inputIndexedDatasets: Record<string, DatasetIdentifierStr[]> = {};
    inputNamedDatasets: Record<string, DatasetIdentifierStr> = {};
    inputNamedCategories: Record<string, number> = {};

    outputIndexedDatasets: Record<string, DatasetIdentifierStr[]> = {};
    outputNamedDatasets: Record<string, DatasetIdentifierStr> = {};
    outputNamedCategories: Record<string, number> = {};

    datasetInputOutputPairs: Record<string, IDatasetInputOutputPair> = {}

    freestyleJson: Record<string, any> = {};

    constructor(data?: Partial<TaskInputGroup>) {
        if (data) {
            Object.assign(this, data)
        }
    }

    static getValueLocation = (inputType: TaskTemplateInputType): (keyof TaskInputGroup | null) => {
        switch (inputType) {
            case TaskTemplateInputType.CONSTANT:
                return "constants" as keyof TaskInputGroup;
            case TaskTemplateInputType.PRODUCT_TEMPLATE:
                return "productTemplates" as keyof TaskInputGroup;
            case TaskTemplateInputType.DATASET_PAIRS:
                return "datasetInputOutputPairs" as keyof TaskInputGroup;

            case TaskTemplateInputType.INPUT_INDEXED_DATASETS:
                return "inputIndexedDatasets" as keyof TaskInputGroup;
            case TaskTemplateInputType.INPUT_NAMED_DATASET:
                return "inputNamedDatasets" as keyof TaskInputGroup;
            case TaskTemplateInputType.INPUT_NAMED_CATEGORY:
                return "inputNamedCategories" as keyof TaskInputGroup;

            case TaskTemplateInputType.OUTPUT_INDEXED_DATASETS:
                return "outputIndexedDatasets" as keyof TaskInputGroup;
            case TaskTemplateInputType.OUTPUT_NAMED_DATASET:
                return "outputNamedDatasets" as keyof TaskInputGroup;
            case TaskTemplateInputType.OUTPUT_NAMED_CATEGORY:
                return "outputNamedCategories" as keyof TaskInputGroup;

            case TaskTemplateInputType.FREESTYLE_JSON:
                return "freestyleJson" as keyof TaskInputGroup;

            default:
                return null;
        }
    }
}

export interface IUserInfo {
    id: number;
    externalId: string;
    username: string;
    firstName?: string;
    lastName?: string;
    displayName: string;
    email?: string;
    companyName: string;
    companyId: number;
    companyRef: string;
}

export interface GeneratedDashboardReportResponse {
    id: number;
    dashboardId: number;
    type: ReportType;
    status: ReportStatus;
    dashboardTitle: string;
    generationTimeStart: string;
    generationTimeEnd: string;
    referenceTime: string;
    scheduleTime: string;
    fileIds: Array<string>;
    generationCount: number
    regenerationCount: number
}

export enum ReportType {
    SCHEDULED= "SCHEDULED",
    MANUAL = "MANUAL"
}

export enum ReportStatus {
    QUEUED = "QUEUED", // smelio laikrodis
    SUCCESS = "SUCCESS", // zalias checkmark
    GENERATING = "GENERATING", // progressspiner
    FAILED = "FAILED", // raudonas iksas
    FAILED_RETRYING = "FAILED_RETRYING", // geltonas trikampis sauktukas
    MARKED_FOR_REGENERATION = "MARKED_FOR_REGENERATION", //

}

export type DashboardInfos = {
    id: number,
    title: string,
    implementation: string
}